import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, cn, FeedbackPopup, computeXpReward, type XpReward } from '@jeraisonne/ui';
import { SUBJECTS } from '@jeraisonne/db';
import { ArrowLeft, ArrowRight, BookOpen, BookOpenCheck, Sparkles } from 'lucide-react';
import { MarkdownMath } from '@jeraisonne/ui';
import { ChoiceButton } from '@/components/exercise/choice-button';
import { PaperChoices, usePaperPlayerForMe } from '@/features/paper-player';
import {
  LEVEL_LABEL,
  type DemoAnswer,
  type DemoDifficulty,
  type DemoExercise,
  type DemoStep,
} from '../types';

/** Métadonnées d'affichage par difficulté — miroir de `DIFF_META` (ExercisePage). */
const DIFF_META: Record<DemoDifficulty, { label: string; cls: string }> = {
  easy: { label: 'Facile', cls: 'bg-xp-50 text-xp-600' },
  medium: { label: 'Moyen', cls: 'bg-level-50 text-level-600' },
  hard: { label: 'Difficile', cls: 'bg-accent-50 text-accent-600' },
};

type StepHistoryEntry = {
  step: DemoStep;
  answer: DemoAnswer;
};

export function ExerciseScreen({
  exercise,
  onAnswer,
  onComplete,
  alreadyAnswered,
  onBack,
}: {
  exercise: DemoExercise;
  onAnswer: (answer: DemoAnswer) => void;
  onComplete: () => void;
  alreadyAnswered: DemoAnswer[];
  /** Bouton retour incrusté dans la couverture. Absent ⇒ pas de bouton. */
  onBack?: () => void;
}) {
  // Reconstruit l'historique à partir des réponses déjà persistées
  // (les ids questions correspondent aux step.id de l'exercice courant).
  const initialHistory: StepHistoryEntry[] = alreadyAnswered
    .map((a) => {
      const step = exercise.steps.find((s) => s.id === a.questionId);
      return step ? { step, answer: a } : null;
    })
    .filter((e): e is StepHistoryEntry => e !== null);

  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();
  const [history, setHistory] = useState<StepHistoryEntry[]>(initialHistory);
  const [currentIdx, setCurrentIdx] = useState(initialHistory.length);
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  // Phase d'intro : on laisse lire la couverture, « La situation » et l'énoncé
  // avant d'afficher la 1re étape ; un exercice repris démarre directement.
  const [started, setStarted] = useState(initialHistory.length > 0);
  // Gamification : série de bonnes réponses + XP cumulé, affichés dans la popup
  // centrée après chaque réponse (même UX que le player). Motivationnel only.
  const [streak, setStreak] = useState(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [reward, setReward] = useState<{
    value: XpReward;
    wasCorrect: boolean;
    explanationMd?: string;
    errorFeedbackMd?: string;
  } | null>(null);

  // Auto-scroll en bas du flux à chaque nouvelle étape ou révélation
  // de feedback (mêmes triggers que le player Solve Path classique).
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);
  useEffect(() => {
    if (revealed) scrollToBottom();
  }, [revealed, scrollToBottom]);
  useEffect(() => {
    // Pas de scroll tant qu'on est en intro : on garde la couverture visible.
    if (started) scrollToBottom();
  }, [currentIdx, started, scrollToBottom]);

  const total = exercise.steps.length;
  const step = exercise.steps[currentIdx];

  if (!step || currentIdx >= total) {
    return <ExerciseSummaryScreen exercise={exercise} history={history} onContinue={onComplete} />;
  }

  const selectedChoice = step.choices.find((c) => c.id === selectedChoiceId) ?? null;

  function handleSelect(choiceId: string) {
    if (revealed || !step) return;
    const choice = step.choices.find((c) => c.id === choiceId);
    if (!choice) return;
    setSelectedChoiceId(choiceId);
    setRevealed(true);
    const answer: DemoAnswer = {
      questionId: step.id,
      choiceId,
      wasCorrect: choice.is_correct,
    };
    onAnswer(answer);
    // Récompense gamifiée → popup centrée (tap = étape suivante).
    const xp = computeXpReward(choice.is_correct, streak);
    setStreak(xp.streak);
    setSessionXp((t) => t + xp.total);
    setReward({
      value: xp,
      wasCorrect: choice.is_correct,
      explanationMd: step.explanation_md,
      errorFeedbackMd: choice.feedback_md ?? undefined,
    });
    // L'historique n'est mis à jour qu'au passage à l'étape suivante,
    // sinon l'étape courante apparaîtrait deux fois pendant la révélation.
  }

  function handleNext() {
    if (!step || !selectedChoice) return;
    setHistory((prev) => [
      ...prev,
      {
        step,
        answer: {
          questionId: step.id,
          choiceId: selectedChoice.id,
          wasCorrect: selectedChoice.is_correct,
        },
      },
    ]);
    setSelectedChoiceId(null);
    setRevealed(false);
    setCurrentIdx((i) => i + 1);
  }

  // Progression : 0 % en intro, puis étapes terminées + l'étape courante.
  const completed = Math.min(currentIdx, total);
  const progress = started ? ((completed + (currentIdx < total ? 1 : 0)) / total) * 100 : 0;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {/* Habillage identique à un exercice réel : couverture, carte titre,
          « La situation » puis énoncé — avant le flux d'étapes. */}
      <ExerciseCover exercise={exercise} progress={progress} onBack={onBack} />
      <ExerciseIntroCard exercise={exercise} currentIdx={currentIdx} total={total} />
      {exercise.context_md && <SituationCard content={exercise.context_md} />}
      {exercise.statement_md && <StatementCard content={exercise.statement_md} />}

      {/* Historique : étapes précédentes (cheminement) */}
      {history.length > 0 && (
        <div className="space-y-2.5">
          {history.map((h, i) => (
            <AnsweredStepCard key={`${h.step.id}-${i}`} entry={h} index={i} />
          ))}
        </div>
      )}

      {started ? (
        /* Étape courante */
        <div
          className={
            paper
              ? 'pp-root pp-page pp-column -mx-4 min-h-0 space-y-3 py-3'
              : 'border-ink-200 space-y-3 rounded-2xl border bg-white p-4'
          }
        >
          <div className="text-ink-400 text-xs font-semibold uppercase tracking-wide">
            Étape {currentIdx + 1}
          </div>
          <div className="text-ink-900 font-sans text-base leading-relaxed">
            <MarkdownMath>{step.prompt_md}</MarkdownMath>
          </div>

          {paper ? (
            <PaperChoices
              questionKey={step.id}
              promptMd={step.prompt_md}
              choices={step.choices}
              selected={selectedChoiceId}
              revealed={revealed}
              busy={false}
              onChoose={(id) => handleSelect(id)}
              storageKey={null}
            />
          ) : (
            <div className="space-y-2">
              {step.choices.map((choice, i) => (
                <ChoiceButton
                  key={choice.id}
                  choice={choice}
                  index={i}
                  selected={selectedChoiceId === choice.id}
                  revealed={revealed}
                  onClick={() => handleSelect(choice.id)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Intro : la 1re étape ne s'affiche qu'au clic sur « Commencer », le
           temps de lire la couverture, « La situation » et l'énoncé. */
        <Button
          onClick={() => setStarted(true)}
          fullWidth
          className="flex items-center justify-center gap-2"
        >
          Commencer l&apos;exercice <ArrowRight className="h-4 w-4" />
        </Button>
      )}

      {/* Feedback gamifié : popup centrée. Un tap n'importe où la ferme ET passe
          à l'étape suivante (ou au résumé sur la dernière). */}
      {reward && (
        <FeedbackPopup
          reward={reward.value}
          wasCorrect={reward.wasCorrect}
          sessionXp={sessionXp}
          explanationMd={reward.explanationMd}
          errorFeedbackMd={reward.errorFeedbackMd}
          onClose={() => {
            setReward(null);
            handleNext();
          }}
        />
      )}

      {/* Anchor de scroll : suit le bas du flux exercice */}
      <div ref={bottomRef} aria-hidden="true" />
    </div>
  );
}

// ─── Couverture (illustration + progression) ─────────────────────────────────

/** Pastille blanche translucide incrustée dans la couverture. */
function CoverChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm backdrop-blur-sm',
        className ?? 'text-ink-700 bg-white/90',
      )}
    >
      {children}
    </span>
  );
}

/**
 * Bandeau de couverture identique à l'écran d'exercice réel : illustration
 * (ou dégradé de repli) en `aspect-video`, dégradé blanc en bas et barre de
 * progression incrustée. Reproduit `ExerciseHeader` de `ExercisePage`,
 * enrichi d'un bouton retour et des pastilles matière / niveau / difficulté.
 */
function ExerciseCover({
  exercise,
  progress,
  onBack,
}: {
  exercise: DemoExercise;
  progress: number;
  onBack?: () => void;
}) {
  const subject = exercise.subject
    ? (SUBJECTS.find((s) => s.value === exercise.subject) ?? null)
    : null;
  const levelLabel = exercise.level ? LEVEL_LABEL[exercise.level] : null;
  const diff = exercise.difficulty ? DIFF_META[exercise.difficulty] : null;
  const hasChips = !!(subject || levelLabel || diff);

  return (
    <div className="from-primary-50 bg-linear-to-br to-ink-50 relative aspect-video w-full overflow-hidden rounded-2xl">
      {exercise.cover_url && (
        <img
          src={exercise.cover_url}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div
        aria-hidden="true"
        className="bg-linear-to-t from-ink-50 pointer-events-none absolute inset-x-0 bottom-0 h-1/3 to-transparent"
      />

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour"
          className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#ffffff]/90 text-[#0d0b06] shadow-md backdrop-blur-sm transition-transform active:scale-95"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}

      {hasChips && (
        <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
          {subject && (
            <CoverChip>
              <span aria-hidden="true">{subject.emoji}</span> {subject.label}
            </CoverChip>
          )}
          {levelLabel && <CoverChip>{levelLabel}</CoverChip>}
          {diff && <CoverChip className={cn('font-bold', diff.cls)}>{diff.label}</CoverChip>}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 px-4 pb-3">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/70 shadow-sm backdrop-blur-sm">
          <div
            className="bg-primary-400 h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Carte titre (chapitre + titre + méta) ────────────────────────────────────

/** Carte titre calquée sur l'écran d'intro d'un exercice réel. */
function ExerciseIntroCard({
  exercise,
  currentIdx,
  total,
}: {
  exercise: DemoExercise;
  currentIdx: number;
  total: number;
}) {
  return (
    <div className="border-ink-200 space-y-2 rounded-2xl border bg-white p-5">
      {exercise.chapter_label && (
        <p className="text-primary-600 text-xs font-bold uppercase tracking-wider">
          {exercise.chapter_label}
        </p>
      )}
      <h2 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
        {exercise.title}
      </h2>
      <div className="text-ink-400 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-xs font-semibold">
        <span className="inline-flex items-center gap-1.5">
          <BookOpenCheck className="text-primary-500 h-3.5 w-3.5" /> Exercice guidé
        </span>
        {exercise.estimated_minutes != null && (
          <span className="font-normal">⏱ {exercise.estimated_minutes} min estimées</span>
        )}
        <span>
          Étape {Math.min(currentIdx + 1, total)}/{total}
        </span>
      </div>
    </div>
  );
}

// ─── « La situation » + énoncé ────────────────────────────────────────────────

/** Carte « La situation » — contexte commun, calquée sur `SituationCard` (ExercisePage). */
function SituationCard({ content }: { content: string }) {
  return (
    <div className="border-primary-200 bg-primary-50/40 rounded-2xl border p-4 sm:p-5">
      <div className="mb-2 flex items-center gap-1.5">
        <BookOpen className="text-primary-500 h-4 w-4 shrink-0" />
        <p className="font-display text-primary-600 text-[11px] font-semibold uppercase tracking-widest">
          La situation
        </p>
      </div>
      <div className="prose prose-sm text-ink-700 max-w-none text-sm">
        <MarkdownMath>{content}</MarkdownMath>
      </div>
    </div>
  );
}

/** Carte « Énoncé » — les notations, complémentaires du contexte. */
function StatementCard({ content }: { content: string }) {
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-5">
      <p className="font-display text-ink-500 mb-2 text-[11px] font-semibold uppercase tracking-widest">
        Énoncé
      </p>
      <div className="prose prose-sm text-ink-700 max-w-none text-sm">
        <MarkdownMath>{content}</MarkdownMath>
      </div>
    </div>
  );
}

// ─── Carte d'étape déjà répondue (collapsible) ──────────────────────────────

function AnsweredStepCard({ entry, index }: { entry: StepHistoryEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const { step, answer } = entry;
  const correctChoice = step.choices.find((c) => c.is_correct);
  const selectedChoice = step.choices.find((c) => c.id === answer.choiceId);

  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      // Carte neutre « verre » comme le player réel (PastStepCard) : plus de
      // fond vert/rouge plein. Le statut tient au badge ✓/✗ dans le coin.
      className="border-ink-100 relative w-full space-y-2 rounded-2xl border bg-white p-3.5 pr-12 text-left transition-transform active:scale-[.98]"
      aria-expanded={expanded}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-ink-400 text-[11px] font-semibold uppercase tracking-wide">
          Étape {index + 1}
        </span>
        <span
          className={cn(
            'text-[11px] font-semibold uppercase tracking-wide',
            answer.wasCorrect ? 'text-xp-600' : 'text-error-600',
          )}
        >
          {answer.wasCorrect ? '· Réussie' : '· À revoir'}
        </span>
      </div>

      <div className="text-ink-800 line-clamp-2 font-sans text-sm">
        <MarkdownMath>{step.prompt_md}</MarkdownMath>
      </div>

      {correctChoice && (
        <div className="flex items-start gap-2">
          <span className="text-xp-600 mt-0.5 shrink-0 text-xs font-bold">✓</span>
          <div className="text-xp-700 flex-1 font-sans text-sm">
            <MarkdownMath>{correctChoice.label_md}</MarkdownMath>
          </div>
        </div>
      )}

      {expanded && (
        <div className="space-y-2 pt-1">
          {/* Énoncé complet, au cas où prompt long */}
          <div className="text-ink-700 font-sans text-sm">
            <MarkdownMath>{step.prompt_md}</MarkdownMath>
          </div>

          {!answer.wasCorrect && selectedChoice && (
            <div className="flex items-start gap-2">
              <span className="text-error-600 mt-0.5 shrink-0 text-xs font-bold">✗</span>
              <div className="text-error-700 flex-1 font-sans text-sm">
                <MarkdownMath>{selectedChoice.label_md}</MarkdownMath>
              </div>
            </div>
          )}

          <div className="bg-ink-50 border-ink-100 rounded-lg border px-3 py-2 text-sm">
            <p className="text-ink-400 mb-1 text-[10px] font-semibold uppercase tracking-wide">
              Explication
            </p>
            <MarkdownMath>{step.explanation_md}</MarkdownMath>
          </div>
        </div>
      )}

      <span
        aria-hidden="true"
        className={cn(
          'absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center text-2xl font-extrabold',
          answer.wasCorrect ? 'text-xp-500' : 'text-error-500',
        )}
      >
        {answer.wasCorrect ? '✓' : '✗'}
      </span>
    </button>
  );
}

// ─── Fin de l'aperçu (incitation à l'inscription) ────────────────────────────

function ExerciseSummaryScreen({
  exercise,
  history,
  onContinue,
}: {
  exercise: DemoExercise;
  history: StepHistoryEntry[];
  onContinue: () => void;
}) {
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="space-y-3 text-center">
        <div className="bg-primary-50 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
          <Sparkles className="text-primary-500 h-8 w-8" />
        </div>
        <p className="text-accent-500 text-xs font-bold uppercase tracking-wider">Aperçu terminé</p>
        <h2 className="text-ink-900 font-serif text-2xl font-semibold sm:text-3xl">
          Pour continuer, inscris-toi !
        </h2>
        <p className="text-ink-700 mx-auto max-w-sm text-sm">
          Tu as découvert comment Jeraisonne décompose un exercice étape par étape. Crée ton compte
          gratuit pour finir cet exercice et débloquer tous les autres.
        </p>
      </div>

      {/* Récapitulatif du cheminement déjà parcouru */}
      {history.length > 0 && (
        <section className="space-y-2.5">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Récapitulatif · {exercise.title}
          </p>
          {history.map((h, i) => (
            <AnsweredStepCard key={`${h.step.id}-${i}`} entry={h} index={i} />
          ))}
        </section>
      )}

      <Button onClick={onContinue} fullWidth className="flex items-center justify-center gap-2">
        M&apos;inscrire pour continuer <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
